// Foundations
import { Component, forwardRef } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  FormBuilder,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';

export interface PasswordFormValues {
  password: string;
  confirmPassword: string;
}

@Component({
  selector: 'app-password-form',
  templateUrl: './password-form.component.html',
  styleUrls: ['./password-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PasswordFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PasswordFormComponent),
      multi: true
    }
  ]
})
export class PasswordFormComponent implements ControlValueAccessor {

  form: FormGroup;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group(
      {
        password: ['', [
          Validators.required,
          // at least 1 uppercase, 1 lowercase, 1 number, 1 special character, min 8, max 64
          Validators.pattern('^(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\\W_]).{8,64}$')
        ]],
        confirmPassword: ['']
      }, { validator: this.checkPasswords });

    this.form.valueChanges.subscribe(value => {
      this.propagateChange(value);
    });
  }

  // Public Methods
  // ------------------------------------------------------------------------------------

  get f(): any {
    return this.form.controls;
  }

  get value(): PasswordFormValues {
    return this.form.value;
  }

  set value(value: PasswordFormValues) {
    this.form.setValue(value);
    this.propagateChange(value);
  }

  registerOnChange(fn): void {
    this.propagateChange = fn;
  }

  writeValue(value): void {
    if (value) {
      this.value = value;
    }
  }

  // not used; leave empty
  registerOnTouched(fn): void {
  }

  propagateChange = (_: any) => { };

  // Required for parent form to determine if this form is valid
  validate(_: FormControl): any {
    return this.form.valid ? null : { passwordForm: { valid: false } };
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private checkPasswords(group: FormGroup): { [k: string]: any } {
    const errors: { [k: string]: any } = {};
    const pass = group.controls.password.value;
    const confirmPass = group.controls.confirmPassword.value;

    // Check if password includes at least 1 uppercase
    const uppercaseRegex = /^(?=.*[A-Z]).{1,}$/;
    if (!uppercaseRegex.test(pass)) {
      errors.missingUppercase = true;
    }

    // Check if password includes at least 1 lowercase
    const lowercaseRegex = /^(?=.*[a-z]).{1,}$/;
    if (!lowercaseRegex.test(pass)) {
      errors.missingLowercase = true;
    }

    // Check if password includes at least 1 number
    const numberRegex = /^(?=.*\d).{1,}$/;
    if (!numberRegex.test(pass)) {
      errors.missingNumber = true;
    }

    // Check if password includes at least 1 special character
    const specialCharRegex = /^(?=.*[\W_]).{1,}$/;
    if (!specialCharRegex.test(pass)) {
      errors.missingSpecialChar = true;
    }

    // Check if password length is at least 8 characters
    const minLenRegex = /^.{8,}$/;
    if (!minLenRegex.test(pass)) {
      errors.minLength = true;
    }

    // Check if passwords match
    if (pass !== confirmPass) {
      errors.notSame = true;
    }

    return errors;
  }
}
